import { useState } from "react";
import {
  impersonateMember,
  isImpersonationAllowedForSession,
  isImpersonationEnabled,
} from "@/api/devImpersonation";
import { B2B_USER_TYPE } from "@/types/b2bAccount";
import type { LayoutPayload } from "@/types/layout";
import styles from "./ImpersonationWidget.module.css";

interface ImpersonationWidgetProps {
  layoutData: LayoutPayload;
  /** Locale-prefixed B2C my-account URL, the landing page after impersonating. */
  myAccountPath: string;
  /** Locale-prefixed B2B portal URL, used when the impersonated member is B2B. */
  b2bMyAccountPath: string;
}

/**
 * Floating dev-only panel for signing in as another member (local/test).
 * Renders nothing unless ENABLE_IMPERSONATION is set in env-config.json.
 * After a successful impersonation it does a full page navigation to the
 * member's account page so the layout (and its auth state) refetches with
 * the new session cookie. Deliberately not localized — internal tooling.
 *
 * Outside a Vite dev build the widget only appears for a Natlink staff session,
 * mirroring the backend guard — impersonating spends that session, so the
 * button disappears once you are signed in as the target member and comes back
 * after logging in as Natlink again.
 */
export function ImpersonationWidget({
  layoutData,
  myAccountPath,
  b2bMyAccountPath,
}: ImpersonationWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sessionEmail = layoutData.isUserAuthenticated
    ? (layoutData.userEmail ?? null)
    : null;

  // Nothing at all — not even the toggle — unless this environment has the
  // tooling switched on AND the current session is allowed to use it.
  if (
    !isImpersonationEnabled() ||
    !isImpersonationAllowedForSession(sessionEmail)
  ) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    const result = await impersonateMember(trimmed);
    if (!result.ok) {
      setError(result.error);
      setIsSubmitting(false);
      return;
    }

    // Full navigation (not client-side routing) so layout/auth state refetches.
    window.location.assign(
      result.data.userType === B2B_USER_TYPE ? b2bMyAccountPath : myAccountPath
    );
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setIsOpen(true)}
      >
        Impersonate
      </button>
    );
  }

  return (
    <section className={styles.panel} aria-label="Impersonate user">
      <div className={styles.header}>
        <h2 className={styles.heading}>Impersonate user</h2>
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={() => setIsOpen(false)}
        >
          ×
        </button>
      </div>
      <p className={styles.current}>
        Signed in as:{" "}
        <strong>
          {layoutData.isUserAuthenticated
            ? (sessionEmail ?? "unknown")
            : "not signed in"}
        </strong>
      </p>
      <form className={styles.form} onSubmit={handleSubmit}>
        <label className={styles.label} htmlFor="impersonation-email">
          Member email (or B2B username)
        </label>
        <input
          id="impersonation-email"
          className={styles.input}
          type="text"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="user@example.com"
          autoComplete="off"
        />
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <button
          type="submit"
          className={styles.submit}
          disabled={isSubmitting || !email.trim()}
        >
          {isSubmitting ? "Signing in…" : "Sign in as user"}
        </button>
      </form>
    </section>
  );
}
